import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Clock, CheckCircle2, Award, CalendarClock } from "lucide-react";
import { fetchService, fetchServices } from "@/lib/api";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { toFa } from "@/lib/fa";

export default function CountryDetail() {
  const { slug } = useParams();
  const { data, isLoading } = useQuery({ queryKey: ["service", slug], queryFn: () => fetchService(slug) });
  const others = useQuery({ queryKey: ["services"], queryFn: fetchServices });
  const s = data?.data;

  if (isLoading) return <div className="mx-auto h-[60vh] max-w-5xl animate-pulse rounded-3xl bg-navy-850/50" />;
  if (!s)
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-5 pt-32 text-center">
        <p className="font-display text-2xl font-bold text-gold">کشور مورد نظر یافت نشد</p>
        <Link to="/countries" className="btn-gold" data-testid="country-notfound-back">بازگشت به مقاصد مهاجرتی</Link>
      </div>
    );

  return (
    <>
      <section className="relative flex min-h-[62vh] items-end overflow-hidden" data-testid="country-hero">
        <motion.img initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }} src={s.image} alt={s.name} className="img-treatment absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/60 to-navy-950/30" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-8">
          <Link to="/countries" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-gold" data-testid="country-back-link">
            <ArrowRight size={15} />بازگشت به مقاصد
          </Link>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <span className="rounded-full border border-gold/40 bg-navy-950/70 px-4 py-1.5 font-display text-xs font-black tracking-widest text-gold" dir="ltr">{s.code}</span>
            <h1 className="mt-5 text-4xl font-black sm:text-5xl lg:text-6xl">
              مهاجرت به <span className="gold-text">{s.name}</span>
            </h1>
            <p className="mt-3 max-w-xl text-lg text-slate-300">{s.tagline}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <span className="flex items-center gap-2 rounded-full border border-gold/25 bg-navy-900/80 px-4 py-2 text-sm text-slate-200"><Award size={15} className="text-gold" />نرخ موفقیت: {s.successRate}</span>
              <span className="flex items-center gap-2 rounded-full border border-gold/25 bg-navy-900/80 px-4 py-2 text-sm text-slate-200"><CheckCircle2 size={15} className="text-gold" />پناهندگی و بشر دوستانه برای هر ۴ مقصد</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <Reveal>
          <p className="max-w-3xl text-base leading-9 text-slate-300">{s.desc}</p>
        </Reveal>

        <Reveal className="mt-14">
          <h2 className="font-display text-2xl font-extrabold sm:text-3xl">مسیرهای <span className="gold-text">ویزایی</span> فعال</h2>
        </Reveal>
        <Stagger className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {s.visaTypes.map((v) => (
            <StaggerItem key={v.title}>
              <div className="card-glow flex h-full flex-col rounded-3xl border border-gold/15 bg-navy-850 p-6" data-testid={`country-visa-card-${v.title}`}>
                <span className="w-fit rounded-full bg-gold/10 px-3 py-1 text-[11px] font-bold text-gold">{v.tag}</span>
                <h3 className="mt-4 font-display text-lg font-bold text-slate-50">{v.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-7 text-slate-400">{v.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <div className="mt-16 grid gap-10 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-2xl font-extrabold">گروه ۷ در پرونده‌های <span className="gold-text">{s.name}</span> چه می‌کند؟</h2>
            <div className="mt-6 space-y-4">
              {s.features.map((f) => (
                <p key={f} className="flex items-start gap-3 text-sm leading-7 text-slate-300">
                  <CheckCircle2 size={17} className="mt-1 shrink-0 text-gold" />
                  {f}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass-panel grid grid-cols-3 gap-4 rounded-3xl border border-gold/25 p-8">
              {s.stats.map((st) => (
                <div key={st.label} className="text-center">
                  <p className="font-display text-2xl font-black text-gold sm:text-3xl">{st.value}</p>
                  <p className="mt-2 text-xs text-slate-400">{st.label}</p>
                </div>
              ))}
              <div className="col-span-3">
                <Link to="/contact" className="btn-gold w-full" data-testid={`country-consult-cta-${s.slug}`}>درخواست مشاوره برای {s.name}<ArrowLeft size={16} /></Link>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-16">
          <h3 className="mb-6 font-display text-lg font-bold text-slate-400">سایر مقاصد مهاجرتی گروه ۷</h3>
          <div className="flex flex-wrap gap-3">
            {(others.data?.data ?? []).filter((c) => c.slug !== slug).map((c) => (
              <Link key={c.slug} to={`/countries/${c.slug}`} className="btn-ghost !px-5 !py-2.5 !text-xs" data-testid={`country-other-link-${c.slug}`}>
                مهاجرت به {c.name}
              </Link>
            ))}
          </div>
        </Reveal>
      </section>
    </>
  );
}
